import React from 'react'
import { useHistory } from 'react-router-dom'
import { EventHelper } from '@monorepo/infra'
import { StorefrontCarouselSlide } from '../../../models/homepage-carousel-slide'
import * as S from './styles'

interface HomepageCarouselCardProps {
    slide: StorefrontCarouselSlide
}

function resolveImageUrl(slide: StorefrontCarouselSlide): string | undefined {
    // The backend resolves itemImageUrl to `customThumbnailUrl ?? productDefault`,
    // so if it's set we always prefer it — this lets custom uploads show on event
    // slides too, not just creatives.
    if (slide.itemImageUrl) return slide.itemImageUrl

    // No uploaded URL and no product media → fall back to the static event icon.
    if (slide.itemType === 'event' && slide.itemSubType != null) {
        const label = EventHelper.getEventTypeDescription(slide.itemSubType)
        return `${process.env.PUBLIC_URL}${EventHelper.getImageFile(label)}`
    }
    return undefined
}

const HomepageCarouselCard: React.FC<HomepageCarouselCardProps> = ({ slide }) => {
    const history = useHistory()
    const imageUrl = resolveImageUrl(slide)
    const badgeLabel = slide.itemType === 'event' ? 'Event' : 'Creative'
    const ctaLabel  = slide.itemType === 'event' ? 'Book Event' : 'Personalize'

    const handleCtaClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        const path = slide.itemType === 'event'
            ? `/all-events/details/${slide.itemId}`
            : `/all-products/details/${slide.itemId}`
        history.push(path)
    }

    return (
        <S.Card>
            <S.CardImageWrapper>
                {imageUrl && (
                    <S.CardImage
                        src={imageUrl}
                        alt={slide.itemTitle || badgeLabel}
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                        }}
                    />
                )}

                {/* Dark overlay — fades in on Card hover via data attribute */}
                <S.CardImageOverlay data-overlay />

                {/* CTA button — slides up on Card hover */}
                <S.CardHoverButton data-cta-btn onClick={handleCtaClick}>
                    {ctaLabel}
                </S.CardHoverButton>
            </S.CardImageWrapper>

            <S.CardBody>
                <S.CardTitle>{slide.itemTitle || '—'}</S.CardTitle>
                <S.CardTypeBadge itemType={slide.itemType}>
                    {badgeLabel}
                </S.CardTypeBadge>
            </S.CardBody>
        </S.Card>
    )
}

export default HomepageCarouselCard
